import React, { useEffect, useRef, useState } from 'react';
import SearchIcon from '../../../assets/search-icon.svg';
import CloseIcon from '../../../assets/close-icon.svg';
import AvatarDefault from '../../../assets/medium_default_optimized.png';
import TrendingIcon from '../../../assets/fire-icon.svg';
import VerifiedIcon from '../../../assets/verified-icon.svg';
import FoundingCreatorIcon from '../../../assets/founding_creator_icon.png';
import { Spinner } from '@chakra-ui/react';
import { fetchSearch } from '../../../apis/article.api';
import { useDebounce } from 'use-debounce';
import { useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import { openSideBarAtom } from './atom';
import { useAtomValue } from 'jotai';
import { profileAtom } from '../../Settings/atom';

const HeaderActionComponent = ({ ...rest }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 3,
    search: ''
  });
  const [searchResult, setSearchResult] = useState({});
  const [debouncedSearch] = useDebounce(pagination.search, 500);
  const [loading, setLoading] = useState(false);
  const cacheRef = useRef({});
  const [, setOpenSidebar] = useAtom(openSideBarAtom);
  const profile = useAtomValue(profileAtom);
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const value = e.target.value;
    setPagination((prev) => ({ ...prev, search: value }));
    setIsExpanded(value.length > 0);
  };

  useEffect(() => {
    if (!debouncedSearch) {
      setSearchResult({});
      return;
    }

    const cacheKey = debouncedSearch;

    const fetchSearchResults = async () => {
      setLoading(true);
      try {
        const result = await fetchSearch(pagination, profile?.id);
        cacheRef.current[cacheKey] = result;
        setSearchResult(result);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };

    if (cacheRef.current[cacheKey]) {
      setSearchResult(cacheRef.current[cacheKey]);
    } else {
      fetchSearchResults();
    }
  }, [debouncedSearch, pagination, profile?.id]);

  const handleClickDetailArticle = (id) => {
    navigate(`/articles/${id}`);
    setIsExpanded(false);
    setOpenSidebar(false);
  };

  const handleClickDetailUser = (username) => {
    navigate(username);
    setIsExpanded(false);
    setOpenSidebar(false);
  };

  const handleClickViewAll = () => {
    navigate(`/search?q=${pagination.search}`);
    setIsExpanded(false);
    setPagination((prev) => ({ ...prev, page: 1 }));
    setOpenSidebar(false);
  };

  return (
    <div
      className="w-full truncate absolute top-[-20px] left-[50%] right-[50%] translate-x-[-50%] px-3"
      {...rest}
    >
      <div className="transition-all duration-300 ease-in-out border border-growth-green-500 bg-white rounded-[20px] lg:max-w-[561px] mx-auto py-[7px] px-4">
        {/* Search Input and Icons */}
        <div className="flex items-center overflow-hidden">
          <img src={SearchIcon} alt="Search" className="w-6 h-6" />
          <input
            className="flex-1 mx-4 font-semibold border-none outline-none focus:outline-none placeholder:text-bold-gray"
            type="text"
            placeholder="Search"
            value={pagination.search}
            onChange={handleInputChange}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && pagination.search) {
                handleClickViewAll();
              }
            }}
            style={{
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              overflow: 'hidden'
            }}
          />
          {pagination.search && (
            <img
              src={CloseIcon}
              alt="Close"
              className="w-6 h-6 cursor-pointer"
              onClick={() => {
                setPagination((prev) => ({ ...prev, search: '' }));
                setIsExpanded(false);
              }}
            />
          )}
        </div>

        {/* Accordion Content */}
        {isExpanded && (
          <div className="mt-4 transition-all duration-300 ease-in-out">
            {loading ? (
              <div className="flex justify-center py-4">
                <Spinner size="md" color="growth-green" />
              </div>
            ) : (
              <>
                {searchResult?.creatorsData?.data?.length > 0 ||
                searchResult?.articleData?.data?.length > 0 ? (
                  <>
                    {/* Authors Section */}
                    <div>
                      {searchResult?.creatorsData?.data?.length > 0 && (
                        <p className="text-sm font-[800] py-[10.5px] text-growth-green-900">
                          Writers
                        </p>
                      )}
                      <ul>
                        {searchResult?.creatorsData?.data?.map((item) => (
                          <li
                            key={item.id}
                            className="py-2 px-2.5 rounded-[12px] hover:bg-[#C8E6C999] cursor-pointer flex items-center gap-2 font-[800] text-sm"
                            onClick={() => handleClickDetailUser(item.username)}
                          >
                            <img
                              className="w-6 h-6 rounded-full flex-shrink-0 object-cover"
                              src={item.imgUrl || AvatarDefault}
                              alt=""
                            />
                            {item.firstName}
                            {item.isVerifiedAuthor && <img src={VerifiedIcon} alt="" />}
                            {item.isFoundingCreator && (
                              <img
                                src={FoundingCreatorIcon}
                                alt="Founding Creator"
                                title="Founding Creator"
                                className="w-7 h-7"
                              />
                            )}
                            {item.username && (
                              <span className="font-[600] text-xs text-02-dim-gray">
                                @{item.username}
                              </span>
                            )}
                            {item?.id === profile.id && (
                              <div className="px-2 py-1 rounded-[80px] bg-growth-green-50 text-sm text-growth-green-900 font-[600]">
                                You
                              </div>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Articles Section */}
                    <div>
                      {searchResult?.articleData?.data?.length > 0 && (
                        <p className="text-sm font-[800] py-[10.5px] text-growth-green-900">
                          Articles
                        </p>
                      )}
                      <ul>
                        {searchResult?.articleData?.data?.map((item) => (
                          <li
                            key={item.id}
                            className="py-2 px-2.5 rounded-[12px] hover:bg-[#C8E6C999] cursor-pointer flex items-center gap-2 font-[800] text-sm"
                            onClick={() =>
                              handleClickDetailArticle(item?.alternativeId || item?.id)
                            }
                          >
                            {item.isBreakingNews && (
                              <img src={TrendingIcon} className="w-6 h-6" alt="" />
                            )}
                            <p className="truncate">{item.title}</p>
                          </li>
                        ))}
                      </ul>
                    </div>
                    {(searchResult?.creatorsData?.totalCount > 3 ||
                      searchResult?.articleData?.totalCount > 3) && (
                      <p
                        className="text-sm font-[800] text-growth-green-500 py-2 cursor-pointer"
                        onClick={handleClickViewAll}
                      >
                        View all results
                      </p>
                    )}
                  </>
                ) : (
                  <div className="py-2 px-2.5 whitespace-break-spaces">
                    <p className="text-sm font-[600]">
                      No results for{' '}
                      <span className="text-growth-green-500 text-sm font-[800]">
                        “{pagination.search}”
                      </span>
                    </p>
                    <p className="text-02-dim-gray mt-2 text-sm font-[600]">
                      We couldn't find any matches, but don’t worry! Try different or broader
                      keywords.
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default HeaderActionComponent;
